<template>
	<div class="dynamic-lead-form" :class="{ 'text-bg-dark form-inverse': config?.useDarkTheme }">
		<LoadingIndicator v-if="isLoading"></LoadingIndicator>

		<form v-else-if="!isLoading && !hasError" @submit="onSubmit">
			<div class="row mb-6">
				<div class="col-md-6 mb-6 mb-md-0">
					<div class="form-floating">
						<input
							id="firstName"
							v-model="firstNameValue"
							name="firstName"
							type="text"
							maxlength="50"
							class="form-control"
							:class="firstNameError ? 'is-invalid' : ''"
							placeholder="First Name"
							data-testid="first-name"
							@blur="firstNameBlur($event, true)"
						/>
						<label for="firstName">First Name&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="first-name-error">
							{{ firstNameError }}
						</div>
					</div>
				</div>
				<div class="col-md-6">
					<div class="form-floating">
						<input
							id="lastName"
							v-model="lastNameValue"
							name="lastName"
							type="text"
							maxlength="50"
							class="form-control"
							:class="lastNameError ? 'is-invalid' : ''"
							placeholder="Last Name"
							data-testid="last-name"
							@blur="lastNameBlur($event, true)"
						/>
						<label for="lastName">Last Name&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="last-name-error">
							{{ lastNameError }}
						</div>
					</div>
				</div>
			</div>
			<div class="row mb-6">
				<div class="col">
					<div class="form-floating">
						<input
							id="phone"
							v-model="phoneValue"
							v-maska="phoneMaskaOptions"
							name="telephone2"
							type="text"
							class="form-control"
							:class="phoneMeta.touched && phoneError ? 'is-invalid' : ''"
							placeholder="Phone"
							data-testid="phone"
							@blur="phoneBlur($event, true)"
						/>
						<label for="phone">Phone&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="phone-error">
							{{ phoneError }}
						</div>
					</div>
				</div>
			</div>

			<div class="row mb-6">
				<div class="col">
					<div class="form-floating">
						<input
							id="email"
							v-model="emailValue"
							name="emailAddress"
							type="text"
							maxlength="140"
							class="form-control"
							:class="emailMeta.touched && emailError ? 'is-invalid' : ''"
							placeholder="email"
							data-testid="email"
							@blur="emailBlur($event, true)"
						/>
						<label for="email">Email&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="email-error">
							{{ emailError }}
						</div>
					</div>
				</div>
			</div>

			<fieldset v-if="config?.showMembershipType" class="mb-6">
				<legend
					class="h6"
					:class="{ 'is-invalid text-danger': typeError }"
					data-testid="membership-type-label"
				>
					Which membership are you interested in?<span v-if="config.requireMembershipType"
						>&ast;</span
					>
				</legend>
				<div
					v-if="typeError"
					class="invalid-feedback mt-1 mb-2"
					data-testid="membership-type-error"
				>
					{{ typeError }}
				</div>

				<div
					v-for="membershipType in membershipTypes"
					:key="membershipType.waitListItemId"
					class="card mb-3"
				>
					<div class="card-body d-flex">
						<div class="form-check mx-1">
							<input
								:id="membershipType.waitListItemId"
								v-model="typeValue"
								class="form-check-input"
								name="type"
								:value="membershipType.crmvalue"
								type="radio"
								:data-testid="`membership-type-${membershipType.waitListItemId}`"
							/>
						</div>
						<div class="flex-fill">
							<EnhanceFieldCollapse
								:membership-type="membershipType"
								:starting-price="startingPrice"
							/>
						</div>
					</div>
				</div>
			</fieldset>

			<fieldset v-if="config?.showMemberCount">
				<legend class="h6" data-testid="member-count-label">How Many Members?</legend>
				<div class="row mb-6">
					<div class="col-md-6 mb-6 mb-md-0">
						<div class="form-floating">
							<select
								id="adults"
								v-model="adultsValue"
								class="form-select"
								:class="{ 'is-invalid': adultsError }"
								aria-label="Adults 14 and older"
								data-testid="adults"
							>
								<option :value="null" disabled>Select number of adults</option>
								<option v-for="(value, index) in adults" :key="index" :value="value">
									{{ value }}
								</option>
							</select>
							<label for="adults">
								Adults 14 and older<span v-if="config.requireMemberCount">&ast;</span>
							</label>
							<div class="invalid-feedback mt-1" data-testid="adults-error">
								{{ adultsError }}
							</div>
						</div>
					</div>
					<div class="col-md-6">
						<div class="form-floating">
							<select
								id="juniors"
								v-model="juniorsValue"
								class="form-select"
								:class="{ 'is-invalid': juniorsError }"
								aria-label="Juniors 13 and under"
								data-testid="juniors"
							>
								<option :value="null" disabled>Select number of juniors</option>
								<option v-for="(value, index) in juniors" :key="index" :value="value">
									{{ value }}
								</option>
							</select>
							<label for="juniors">
								Juniors 13 and under<span v-if="config.requireMemberCount">&ast;</span>
							</label>
							<div class="invalid-feedback mt-1" data-testid="juniors-error">
								{{ juniorsError }}
							</div>
						</div>
					</div>
				</div>
			</fieldset>

			<div v-if="config?.showInterests" class="row mb-6">
				<div class="col">
					<label for="adults" class="h6" data-testid="interests-label">
						What are you most interested in?<span v-if="config.requireInterests">&ast;</span>
					</label>
					<select
						id="interests"
						v-model="interestsValue"
						class="form-select"
						:class="{ 'is-invalid': interestsError }"
						aria-label="What are you most interested in?"
						data-testid="interests"
					>
						<option disabled :value="null">Select one interest</option>
						<option v-for="interest in interests" :key="interest.value" :value="interest.value">
							{{ interest.label }}
						</option>
					</select>
					<div class="invalid-feedback mt-1" data-testid="interests-error">
						{{ interestsError }}
					</div>
				</div>
			</div>

			<div class="row mb-6">
				<div class="col">
					<div class="form-check ps-0">
						<input
							id="tou"
							v-model="touValue"
							name="tou"
							class="form-check-input ms-0"
							:class="touError ? 'is-invalid' : ''"
							:value="false"
							type="checkbox"
							data-testid="tou"
						/>
						<label class="form-check-label small text-opacity-75 d-block ps-3" for="tou">
							{{ config?.tocText }}
						</label>
						<div class="invalid-feedback mt-1" data-testid="tou-error">{{ touError }}</div>
					</div>
				</div>
				<slot name="touLinks" />
			</div>

			<div class="d-grid gap-2">
				<button
					v-adobe-data-layer="joinADLConfig"
					class="btn btn-dark"
					data-testid="submit-btn"
					type="submit"
				>
					{{ config?.submitBtnText }}
				</button>
			</div>
			<input name="form-type" type="hidden" :value="config?.formType" />
			<input name="email-template" type="hidden" :value="config?.emailTemplateId" />
			<input name="inquiry-type" type="hidden" :value="config?.inquiryType" />
		</form>

		<div v-if="hasError" class="invalid-feedback d-block">There was a issue loading the form</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, type Ref } from 'vue';
import { vMaska } from 'maska/vue';
import { useField, useForm } from 'vee-validate';
import LoadingIndicator from 'Lib/components/shared/LoadingIndicator.vue';
import EnhanceFieldCollapse from 'Lib/components/lead-form/EnhancedFieldCollapse.vue';
import { useMaska } from '@/composables/use-maska';
import { aemServletService } from '@/services/aemServletService';
import { leadFormService } from '@/services/leadFormService';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import { club } from '@/util/club';
import { logError, logWarn } from '@/util/logger';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import type {
	DynamicLeadFormAuthoredContent,
	IFormData
} from 'Types/aemui/lead-form/aemuiLeadForm';
import type {
	AemuiLeadFormConfig,
	AemuiWaitlistInterest,
	AemuiWaitlistItem
} from 'Types/aemui/content-fragments/contentFragment';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { validateEmail, validateName, validatePhone } from '@/util/validators';
import { getUtmParams } from '@/util/utm';

const authoredContent: Ref<DynamicLeadFormAuthoredContent> = ref({});
const config: Ref<AemuiLeadFormConfig | null> = ref(null);
const clubData: Ref<ltFramework.IClubDetailsFromDOM> = ref(club);
const interests: Ref<AemuiWaitlistInterest[]> = ref([]);
const hasError = ref<boolean>(false);
const isLoading = ref<boolean>(true);
const membershipTypes: Ref<AemuiWaitlistItem[]> = ref([]);
const startingPrice: Ref<string | null> = ref(null);

const setConfigType = (
	config: AemuiLeadFormConfig,
	variations: AemuiLeadFormConfig[],
	type: string
) => {
	const variation = variations.find((v) => v.formType === type);
	if (variation) {
		config = { ...config, ...variation };
	} else {
		logWarn(`lead form config could not find form type variation: ${type}`, {
			variations: variations
		});
	}
	return config;
};

const getConfig = async () => {
	try {
		const variations = (await aemServletService.getLeadFormConfig(clubData.value.mmsClubId)).data
			.variations;
		let config = variations.find((v) => v.formType === 'open');
		if (!config) {
			throw new Error('Default lead form config was not found');
		}
		if (authoredContent.value.formTypeConfig) {
			config = setConfigType(config, variations, authoredContent.value.formTypeConfig);
		} else if (clubData.value.open === 'open' && clubData.value.webPreSaleWaitlist === '1') {
			config = setConfigType(config, variations, 'openWaitlist');
		} else if (clubData.value.open === 'presale' && clubData.value.webPreSaleWaitlist === '1') {
			config = setConfigType(config, variations, 'presale');
		} else if (clubData.value.open === 'waitlist' && clubData.value.webPreSaleWaitlist === '1') {
			config = setConfigType(config, variations, 'waitlist');
		}
		return config;
	} catch (error: unknown) {
		hasError.value = true;
		logError('GET lead form config failed', { clubId: clubData.value.mmsClubId }, error);
		return null;
	}
};

onBeforeMount(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	authoredContent.value = getAuthoredContent<DynamicLeadFormAuthoredContent>(
		'aemuiDynamicLeadFormScript'
	);

	if (clubData.value.mmsClubId) {
		config.value = await getConfig();

		try {
			if (config.value?.showMembershipType) {
				const response = await aemServletService.getWaitlistItems(clubData.value.mmsClubId);
				startingPrice.value = response.data.club.startingPriceString;
				membershipTypes.value = response.data.variations;
			}
			if (config.value?.showInterests) {
				const interestRes = await aemServletService.getWaitlistInterests(clubData.value.mmsClubId);
				interests.value = interestRes.data.variations;
			}
		} catch (error: unknown) {
			logError('GET waitlist items failed', { clubId: clubData.value.mmsClubId }, error);
		}
	}
	isLoading.value = false;
});

const { handleSubmit } = useForm<IFormData>({
	initialValues: {
		addressLatitude: '',
		addressLongitude: '',
		birthYear: '',
		channel: 'AEM',
		clubStatus: '',
		clubType: 'club',
		communicationConsent: '',
		consentDateAndTime: '',
		consentIPAddress: '',
		consentText: '',
		currentMember: '',
		dcmp: '',
		description: '',
		device: '',
		emailAddress: '',
		emailTemplate: config.value?.emailTemplateId,
		gcId: '',
		gcLId: '',
		group: '',
		inquiryType: config.value?.inquiryType, // Maps to LeadSource in CRM
		interests: '',
		keyWords: '',
		landingPage: `${document.URL}`,
		leadType: 'Web',
		membershipInfoRequested: 'Wait List',
		mmsClubId: club.mmsClubId.toString(),
		operatingSystem: '',
		referringDomain: `${document.location.origin}`,
		referringPage: `${document.location.pathname}`,
		subject: '',
		telephone2: '',
		tou: false,
		visitCount: '',
		visitorId: ''
	}
});

const {
	errorMessage: firstNameError,
	handleBlur: firstNameBlur,
	value: firstNameValue
} = useField<string>('firstName', (value: string) =>
	validateName(value, 'Please enter your first name')
);

const {
	errorMessage: lastNameError,
	handleBlur: lastNameBlur,
	value: lastNameValue
} = useField<string>('lastName', (value: string) =>
	validateName(value, 'Please enter your last name')
);

const { phoneMaskaOptions } = useMaska();
const {
	errorMessage: phoneError,
	handleBlur: phoneBlur,
	value: phoneValue,
	meta: phoneMeta
} = useField<string>('telephone2', (value: string) =>
	validatePhone(value, 'Please enter your phone number')
);

const {
	errorMessage: emailError,
	handleBlur: emailBlur,
	value: emailValue,
	meta: emailMeta
} = useField<string>('emailAddress', (value: string) =>
	validateEmail(value, 'Please enter an email address')
);

const { errorMessage: typeError, value: typeValue } = useField<string>(
	'membershipType',
	(value) => {
		if (!config.value?.requireMembershipType) {
			return true;
		}
		return value ? true : 'Please select a membership';
	},
	{ initialValue: undefined }
);

const adults = [1, 2, 3, 4];
const { errorMessage: adultsError, value: adultsValue } = useField<number | null>(
	'numberOfAdults',
	(value: number | null) => {
		if (!config.value?.requireMemberCount) {
			return true;
		}
		return value ? true : 'Please select a value';
	},
	{ initialValue: null }
);

const juniors = [0, 1, 2, 3, 4, 5, 6];
const { errorMessage: juniorsError, value: juniorsValue } = useField<number | null>(
	'numberOfJuniors',
	(value: number | null) => {
		if (!config.value?.requireMemberCount) {
			return true;
		}
		return typeof value === 'number' ? true : 'Please select a value';
	},
	{ initialValue: null }
);

const { errorMessage: interestsError, value: interestsValue } = useField<string | null>(
	'interests',
	(value) => {
		if (!config.value?.requireInterests) {
			return true;
		}
		return value ? true : 'Please select an interest';
	},
	{ initialValue: null }
);

const { errorMessage: touError, value: touValue } = useField<boolean>('tou', (value: boolean) =>
	value ? true : 'Please accept the terms'
);

const onSubmit = handleSubmit(async (values) => {
	if (!config.value) {
		return;
	}

	// hidden values on the form we populate
	values = {
		...values,
		...getUtmParams(),
		consentDateAndTime: document.lastModified,
		consentText: config.value?.tocText,
		emailTemplate: config.value.emailTemplateId,
		interests: values.interests,
		inquiryType: config.value.inquiryType,
		mmsClubId: clubData.value.mmsClubId.toString()
	};

	if (!config.value?.showMemberCount) {
		delete values.numberOfAdults;
		delete values.numberOfJuniors;
	}

	if (config.value?.membershipType) {
		values.membershipType = config.value.membershipType;
	} else if (typeValue.value) {
		values.membershipType = typeValue.value;
	}

	try {
		isLoading.value = true;
		await leadFormService.submitForm(values);
		if (config.value.confirmationUrl) {
			window.location.assign(config.value.confirmationUrl);
		}
	} catch (error: unknown) {
		logError('Lead Form submit error', { values }, error);
		if (config.value.errorUrl) {
			window.location.assign(config.value.errorUrl);
		}
	} finally {
		isLoading.value = false;
	}
});

const joinADLConfig = {
	name: 'leadform',
	type: 'button',
	title: 'Join the waitlist',
	link: config.value?.confirmationUrl
};

defineExpose({
	adultsValue,
	clubData,
	config,
	hasError,
	interests,
	isLoading,
	juniorsValue,
	membershipTypes,
	startingPrice,
	typeValue
});
</script>

<style lang="scss" scoped>
.card {
	border: 0;

	.dynamic-lead-form & {
		background-color: var(--kpl-contrast-200);
	}

	.dynamic-lead-form.form-inverse & {
		background-color: $gray-700;

		:deep(.btn-chevron) {
			color: var(--kpl-off-white);
		}
	}

	&-body {
		padding-bottom: 1.5rem;
		padding-top: 1.5rem;
	}

	:deep(.membership-description p:last-child) {
		margin-bottom: 0;
	}
}

.dynamic-lead-form.form-inverse {
	.form-select {
		color-scheme: dark;
	}

	.form-select option {
		color: var(--kpl-off-white);
		background-color: $gray-700;
	}
}
</style>
